import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Employee, Shift, User, UserLevel } from "../lib/types";
import { Button } from "../components/ui/Button";
import { Field, Input, Select } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { RemoveButton } from "../components/ui/RemoveButton";
import { Avatar } from "../components/ui/Avatar";
import { can, LEVEL_LABEL, levelRank } from "../lib/permissions";
import { useEffectiveUser } from "../lib/auth";

const empty: Partial<Employee> = {
  firstName: "",
  lastName: "",
  badgeNumber: "",
  radioNumber: "",
  rank: "",
  status: "active",
  isOic: false,
  isFto: false,
};

export function Employees() {
  const me = useEffectiveUser();
  const mayEdit = can.editEmployees(me);
  const mayCreate = can.createEmployees(me);
  const mayInvite = can.sendInvites(me);
  const qc = useQueryClient();
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  // Used to derive the per-row Accepted / Pending / Not invited
  // chip. Profiles carry accepted_at via a trigger on auth.users
  // updates (see migration 0018). Officers without an account row
  // get either "Not invited" or "Pending" depending on invitedAt.
  const users = useQuery({ queryKey: ["users"], queryFn: api.listUsers });
  const userByEmployee = useMemo(() => {
    // Multiple profile rows can link to one employee when the initial
    // invite went to a typo'd email (e.g., "tbills@warrascres" vs
    // "tbills@warracres"). The typo orphan stays in the DB and was
    // clobbering the chip — last user iterated wins.
    //
    // Pick the "best" user per employee: accepted accounts beat
    // pending; among ties, non-disabled beats disabled. This makes
    // the chip show the real status of the active account regardless
    // of how many orphans share the employee_id.
    function score(u: User): number {
      return (u.acceptedAt ? 2 : 0) + (u.disabledAt ? 0 : 1);
    }
    const m = new Map<string, User>();
    for (const u of users.data ?? []) {
      if (!u.employeeId) continue;
      const existing = m.get(u.employeeId);
      if (!existing || score(u) > score(existing)) m.set(u.employeeId, u);
    }
    return m;
  }, [users.data]);

  const [editing, setEditing] = useState<Partial<Employee> | null>(null);
  const [armedId, setArmedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [invitePending, setInvitePending] = useState<Employee | null>(null);

  // Multi-select state for the mass-edit flow. Backed by an id Set
  // so toggling a row is O(1) and the "select all" checkbox can
  // reflect the cross-section of currently-visible rows + selection
  // (none / some / all).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [massEditOpen, setMassEditOpen] = useState(false);
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll(allVisibleIds: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) for (const id of allVisibleIds) next.add(id);
      else for (const id of allVisibleIds) next.delete(id);
      return next;
    });
  }

  const saveMut = useMutation({
    mutationFn: async (e: Partial<Employee>) => {
      const saved = e.id
        ? await api.updateEmployee(e.id, e)
        : await api.createEmployee(e);
      // Re-apply the days-off pattern after any employee save so a
      // change to weekdays / start date / mode immediately
      // materializes XXX rows. The active mode picks between the
      // regular and tac patterns AND the target shift — tac mode's
      // pattern lives on the tac home shift, regular's on the raw
      // home. Pre-fix the materialiser always targeted homeShiftId
      // even for tac mode, which silently did nothing for tac-only
      // officers (Hunt has no regular home but a tac home — the
      // pattern was simply never written).
      const isTac = saved.daysOffMode === "tac";
      const w1 = (isTac ? saved.tacDaysOffWeekdays : saved.daysOffWeekdays) ?? [];
      const w2 = isTac ? saved.tacDaysOffWeekdaysW2 : saved.daysOffWeekdaysW2;
      const sd = saved.daysOffStartDate;
      const targetShift = isTac
        ? (saved.tacHomeShiftId ?? saved.homeShiftId)
        : (saved.homeShiftId ?? saved.tacHomeShiftId);
      // Always run the materialiser when we have a start date so it
      // can either insert new XXX rows OR clean stale ones (targetShift
      // null → materialiser strips every from_pattern row in the
      // window). Without that cleanup pass, removing an officer's
      // home shift left their old pattern XXX behind permanently.
      if (sd) {
        await api.applyDaysOffPattern({
          employeeId: saved.id,
          week1: w1,
          week2: w2,
          startDate: sd,
          homeShiftId: targetShift,
        });
      }
      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      setEditing(null);
      setError(null);
    },
    onError: (err: { message?: string }) => setError(err?.message ?? "Save failed"),
  });

  // Mass-edit: apply a single field patch to every selected
  // employee in a sequential loop. Sequential (not Promise.all)
  // keeps the audit log in deterministic order and lets a partial
  // failure halt early with a clear message rather than racing
  // through. Each save re-uses the same days-off re-materialisation
  // step the single-edit flow runs.
  const massSaveMut = useMutation({
    mutationFn: async (input: { ids: string[]; patch: Partial<Employee> }) => {
      let count = 0;
      for (const id of input.ids) {
        const before = (employees.data ?? []).find((x) => x.id === id);
        if (!before) continue;
        const patch: Partial<Employee> = { ...input.patch };
        // Changing department invalidates the old home shift (shifts
        // are dept-scoped). If the caller didn't explicitly set a
        // new homeShiftId in the patch, blank it out so the row
        // doesn't end up pointing at a shift in the wrong dept.
        if (patch.departmentId
            && before.departmentId !== patch.departmentId
            && !("homeShiftId" in input.patch)) {
          patch.homeShiftId = null;
        }
        const saved = await api.updateEmployee(id, patch);
        const touchedDaysOff =
          "daysOffWeekdays" in input.patch
          || "daysOffWeekdaysW2" in input.patch
          || "tacDaysOffWeekdays" in input.patch
          || "tacDaysOffWeekdaysW2" in input.patch
          || "daysOffMode" in input.patch
          || "daysOffStartDate" in input.patch
          || "homeShiftId" in input.patch
          || patch.homeShiftId === null;
        if (touchedDaysOff) {
          const isTac = saved.daysOffMode === "tac";
          const w1 = (isTac ? saved.tacDaysOffWeekdays : saved.daysOffWeekdays) ?? [];
          const w2 = isTac ? saved.tacDaysOffWeekdaysW2 : saved.daysOffWeekdaysW2;
          const sd = saved.daysOffStartDate;
          const targetShift = isTac
            ? (saved.tacHomeShiftId ?? saved.homeShiftId)
            : (saved.homeShiftId ?? saved.tacHomeShiftId);
          if (sd) {
            await api.applyDaysOffPattern({
              employeeId: saved.id,
              week1: w1,
              week2: w2,
              startDate: sd,
              homeShiftId: targetShift,
              silent: true,
            });
          }
        }
        count += 1;
      }
      return count;
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      setMassEditOpen(false);
      setSelected(new Set());
      setError(null);
      setFlash(`Updated ${count} employee${count === 1 ? "" : "s"}.`);
    },
    onError: (err: { message?: string }) =>
      setError(err?.message ?? "Mass edit failed"),
  });

  const inviteMut = useMutation({
    mutationFn: (id: string) => api.sendInvite(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      setInvitePending(null);
      setError(null);
      setFlash(`Invite email sent to ${res.email}.`);
    },
    onError: (e: { message?: string }) => {
      setInvitePending(null);
      setFlash(null);
      setError(e?.message ?? "Invite failed.");
    },
  });

  // Bypass-the-email path for agencies whose security scanner eats
  // the one-time invite link. Surfaces the generated password to the
  // supervisor so they can pass it along out-of-band.
  const [setPwResult, setSetPwResult] = useState<{
    email: string; password: string;
  } | null>(null);
  const setPasswordMut = useMutation({
    mutationFn: (id: string) => api.setEmployeePassword(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      setSetPwResult(res);
      setInvitePending(null);
      setError(null);
    },
    onError: (e: { message?: string }) => {
      setFlash(null);
      setError(e?.message ?? "Could not set password.");
    },
  });

  function askSendInvite(e: Employee) {
    const primary = e.emails?.[0];
    if (!primary) return;
    setInvitePending(e);
  }

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteEmployee(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["employees"] }),
  });

  // Sortable-column state. Click a header once → sort by that column
  // ascending; click again → flip to descending; click another column
  // → sort by that column ascending. Empty values always sink to the
  // bottom regardless of direction so a page of "—" cells doesn't
  // trap the officers you care about below the fold.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "name",
    dir: "asc",
  });
  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" });
  }
  const sortedEmployees = useMemo(
    () => sortEmployees(employees.data ?? [], shifts.data ?? [], sort),
    [employees.data, shifts.data, sort],
  );

  return (
    <div className="p-4 md:p-6">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Employees</h1>
          <p className="text-sm text-slate-500">{employees.data?.length ?? 0} active</p>
        </div>
        <div className="flex items-center gap-2">
          {mayEdit && selected.size > 0 && (
            <>
              <Button variant="secondary" onClick={() => setSelected(new Set())}>
                Clear selection
              </Button>
              <Button onClick={() => setMassEditOpen(true)}>
                Edit {selected.size} selected
              </Button>
            </>
          )}
          {mayCreate && (
            <Button onClick={() => setEditing(empty)}>+ New employee</Button>
          )}
        </div>
      </header>

      {error && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-700 font-semibold leading-none"
            title="Dismiss"
          >×</button>
        </div>
      )}
      {flash && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <span className="flex-1">{flash}</span>
          <button
            type="button"
            onClick={() => setFlash(null)}
            className="text-emerald-400 hover:text-emerald-700 font-semibold leading-none"
            title="Dismiss"
          >×</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              {mayEdit && (
                <th className="px-3 py-2 w-8">
                  <SelectAllCheckbox
                    visibleIds={sortedEmployees.map((e) => e.id)}
                    selected={selected}
                    onToggleAll={toggleAll}
                  />
                </th>
              )}
              <SortHeader label="Name" sortKey="name" sort={sort} onToggle={toggleSort} />
              <SortHeader label="Badge #" sortKey="badge" sort={sort} onToggle={toggleSort} />
              <SortHeader label="Radio #" sortKey="radio" sort={sort} onToggle={toggleSort} />
              <SortHeader label="Rank" sortKey="rank" sort={sort} onToggle={toggleSort} />
              <SortHeader label="Status" sortKey="status" sort={sort} onToggle={toggleSort} />
              <SortHeader label="OIC" sortKey="oic" sort={sort} onToggle={toggleSort} />
              <SortHeader label="Level" sortKey="level" sort={sort} onToggle={toggleSort} />
              <SortHeader label="Home shift" sortKey="shift" sort={sort} onToggle={toggleSort} />
              <SortHeader label="12-hour home shift" sortKey="tacShift" sort={sort} onToggle={toggleSort} />
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedEmployees.map((e) => {
              const shift = shifts.data?.find((s) => s.id === e.homeShiftId);
              // The tac home shift column only carries a value for
              // officers whose followsTacMode flag is on — for
              // everyone else (dispatch civilians, ACO, etc.) the
              // field is irrelevant since tac mode doesn't move
              // them. Render an em-dash so the column lines up
              // without claiming an absent assignment.
              const tacShift = e.followsTacMode
                ? shifts.data?.find((s) => s.id === e.tacHomeShiftId)
                : null;
              return (
                <tr key={e.id} className={`hover:bg-slate-50 ${selected.has(e.id) ? "bg-sky-50" : ""}`}>
                  {mayEdit && (
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(e.id)}
                        onChange={() => toggleOne(e.id)}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        aria-label={`Select ${e.lastName}, ${e.firstName}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <Avatar photo={e.photo} firstName={e.firstName} lastName={e.lastName} size={28} />
                      <span>{e.lastName}, {e.firstName}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">{e.badgeNumber ?? "—"}</td>
                  <td className="px-3 py-2">{e.radioNumber ?? "—"}</td>
                  <td className="px-3 py-2">{e.rank ?? "—"}</td>
                  <td className="px-3 py-2 capitalize">{e.status}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      {e.isOic && (
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-800 border border-amber-200">
                          OIC
                        </span>
                      )}
                      {e.isFto && (
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-sky-100 text-sky-800 border border-sky-200">
                          FTO
                        </span>
                      )}
                      {!e.isOic && !e.isFto && (
                        <span className="text-slate-300">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-xs font-mono text-slate-600">
                      {LEVEL_LABEL[e.level]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {shift ? (
                      <span style={{ color: shift.color }}>● {shift.name}</span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {tacShift ? (
                      <span style={{ color: tacShift.color }}>● {tacShift.name}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <InviteStatusChip
                        invitedAt={e.invitedAt}
                        user={userByEmployee.get(e.id)}
                      />
                      {mayInvite && (e.emails ?? []).length > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => askSendInvite(e)}
                          title={e.invitedAt
                            ? `Re-send invite · last ${new Date(e.invitedAt).toLocaleString()}`
                            : `Send invite to ${e.emails[0]}`}
                        >
                          {e.invitedAt ? "Re-invite" : "Invite"}
                        </Button>
                      )}
                      {mayEdit && (
                        <Button size="sm" variant="ghost" onClick={() => setEditing(e)}>Edit</Button>
                      )}
                      {mayCreate && (
                        <RemoveButton
                          armed={armedId === e.id}
                          onArm={() => setArmedId(e.id)}
                          onConfirm={() => { setArmedId(null); deleteMut.mutate(e.id); }}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {sortedEmployees.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm">
            No employees yet. Click <strong>+ New employee</strong> to add one.
          </div>
        )}
      </div>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit employee" : "New employee"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => editing && saveMut.mutate(editing)} disabled={saveMut.isPending}>
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        {editing && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="col-span-2">
              <PhotoUpload
                photo={editing.photo ?? null}
                firstName={editing.firstName ?? ""}
                lastName={editing.lastName ?? ""}
                onChange={(p) => setEditing({ ...editing, photo: p })}
              />
            </div>
            <Field label="First name">
              <Input
                value={editing.firstName ?? ""}
                onChange={(e) => setEditing({ ...editing, firstName: e.target.value })}
              />
            </Field>
            <Field label="Last name">
              <Input
                value={editing.lastName ?? ""}
                onChange={(e) => setEditing({ ...editing, lastName: e.target.value })}
              />
            </Field>
            <Field label="Badge #">
              <Input
                value={editing.badgeNumber ?? ""}
                onChange={(e) => setEditing({ ...editing, badgeNumber: e.target.value })}
              />
            </Field>
            <Field label="Radio #">
              <Input
                value={editing.radioNumber ?? ""}
                onChange={(e) => setEditing({ ...editing, radioNumber: e.target.value })}
                placeholder="e.g. 4-12, A12, K9-2"
              />
            </Field>
            <Field label="Rank">
              <Input
                value={editing.rank ?? ""}
                onChange={(e) => setEditing({ ...editing, rank: e.target.value })}
              />
            </Field>
            <Field label="Hire date">
              <Input
                type="date"
                value={editing.hireDate ?? ""}
                onChange={(e) => setEditing({ ...editing, hireDate: e.target.value })}
              />
            </Field>
            <Field label="Retirement date">
              <Input
                type="date"
                value={editing.retirementDate ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, retirementDate: e.target.value || null })
                }
                title="On or after this date the officer drops off the schedule on every shift family."
              />
            </Field>
            <Field label="Status">
              <Select
                value={editing.status ?? "active"}
                onChange={(e) =>
                  setEditing({ ...editing, status: e.target.value as Employee["status"] })
                }
              >
                <option value="active">Active</option>
                <option value="leave">On leave</option>
                <option value="inactive">Inactive</option>
              </Select>
            </Field>
            <Field label="Access level">
              <LevelSelect
                value={editing.level ?? "l4"}
                targetLevel={(editing.level ?? "l4") as UserLevel}
                onChange={(lv) => setEditing({ ...editing, level: lv })}
              />
            </Field>
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <ContactList
                label="Email"
                type="email"
                values={editing.emails ?? []}
                onChange={(v) => setEditing({ ...editing, emails: v })}
                placeholder="jane@agency.gov"
              />
              <ContactList
                label="Phone"
                type="tel"
                values={editing.phones ?? []}
                onChange={(v) => setEditing({ ...editing, phones: v })}
                placeholder="(555) 555-0123"
              />
            </div>
            <Field label="Department">
              <Select
                value={editing.departmentId ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, departmentId: e.target.value, homeShiftId: null })
                }
              >
                <option value="" disabled>— Pick —</option>
                {(departments.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Home shift">
              <Select
                value={editing.homeShiftId ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, homeShiftId: e.target.value || null })
                }
              >
                <option value="">— None —</option>
                {(shifts.data ?? [])
                  .filter((s) => !editing.departmentId || s.departmentId === editing.departmentId)
                  .map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
              </Select>
            </Field>
            <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
              <DaysOffEditor
                regularW1={editing.daysOffWeekdays ?? []}
                regularW2={editing.daysOffWeekdaysW2 ?? null}
                tacW1={editing.tacDaysOffWeekdays ?? []}
                tacW2={editing.tacDaysOffWeekdaysW2 ?? null}
                mode={editing.daysOffMode ?? "regular"}
                startDate={editing.daysOffStartDate ?? ""}
                onChange={(patch) => setEditing({ ...editing, ...patch })}
              />
              {(() => {
                const isTac = (editing.daysOffMode ?? "regular") === "tac";
                const target = isTac
                  ? (editing.tacHomeShiftId ?? editing.homeShiftId)
                  : (editing.homeShiftId ?? editing.tacHomeShiftId);
                if (target) return null;
                return (
                  <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <strong>No home shift set.</strong> The pattern is stored
                    but won't appear on the schedule until you assign a
                    {isTac ? " 12-hour home shift" : " Home shift"} above.
                  </div>
                );
              })()}
            </div>
            <div className="col-span-2 border-t border-slate-200 pt-3 mt-1 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={editing.followsTacMode ?? false}
                  onChange={(e) =>
                    setEditing({ ...editing, followsTacMode: e.target.checked })
                  }
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span>
                  <span className="font-medium">Uses 12-hour Shifts</span>
                  <span className="block text-xs text-slate-500">
                    When the agency activates 12-hour Shifts, this officer
                    swaps to their 12-hour home shift and 12-hour days-off
                    pattern. Leave off for officers who stay on their
                    regular schedule (dispatch civilians, etc.).
                  </span>
                </span>
              </label>
              {(editing.followsTacMode ?? false) && (
                <Field label="12-hour home shift">
                  <Select
                    value={editing.tacHomeShiftId ?? ""}
                    onChange={(e) =>
                      setEditing({ ...editing, tacHomeShiftId: e.target.value || null })
                    }
                  >
                    <option value="">— None —</option>
                    {(shifts.data ?? [])
                      .filter((s) => s.tacRole === "tac")
                      .map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                  </Select>
                  <p className="mt-1 text-xs text-slate-500">
                    Only shifts marked as "12-hour shift" appear here. Create
                    one on the Shifts page first if the list is empty.
                  </p>
                </Field>
              )}
            </div>
            <label className="col-span-2 flex items-center gap-2 cursor-pointer text-sm text-slate-700 mt-1">
              <input
                type="checkbox"
                checked={editing.isOic ?? false}
                onChange={(e) =>
                  setEditing({ ...editing, isOic: e.target.checked })
                }
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                <span className="font-medium">Officer in Charge (OIC)</span>
                <span className="block text-xs text-slate-500">
                  Eligible to lead a shift. Counted separately from total
                  staffing.
                </span>
              </span>
            </label>
            <label className="col-span-2 flex items-center gap-2 cursor-pointer text-sm text-slate-700 mt-1">
              <input
                type="checkbox"
                checked={editing.isFto ?? false}
                onChange={(e) =>
                  setEditing({ ...editing, isFto: e.target.checked })
                }
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                <span className="font-medium">In training (FTO)</span>
                <span className="block text-xs text-slate-500">
                  Rides along with a training officer. Not counted toward
                  shift coverage or shortage.
                </span>
              </span>
            </label>
            {editing.id && (
              <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                <ShiftAssignmentsEditor
                  employeeId={editing.id}
                  shifts={shifts.data ?? []}
                />
              </div>
            )}
            {editing.id && (
              <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                <IodPeriodsEditor
                  employee={editing as Employee}
                />
              </div>
            )}
            {editing.id && (
              <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                <ClearProceduralShifts
                  employeeIds={[editing.id]}
                  label={`Clear ${editing.firstName ?? ""}'s procedural shifts`}
                  onCleared={() => setEditing(null)}
                />
              </div>
            )}
            {error && <div className="col-span-2 text-sm text-red-600">{error}</div>}
          </div>
        )}
      </Modal>

      <MassEditModal
        open={massEditOpen}
        count={selected.size}
        employeeIds={[...selected]}
        departments={departments.data ?? []}
        shifts={shifts.data ?? []}
        pending={massSaveMut.isPending}
        onClose={() => { if (!massSaveMut.isPending) setMassEditOpen(false); }}
        onApply={(patch) =>
          massSaveMut.mutate({ ids: [...selected], patch })}
      />

      <Modal
        open={invitePending !== null}
        onClose={() => {
          if (!inviteMut.isPending && !setPasswordMut.isPending) setInvitePending(null);
        }}
        title={invitePending?.invitedAt ? "Re-send invite?" : "Send invite?"}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setInvitePending(null)}
              disabled={inviteMut.isPending || setPasswordMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => invitePending && setPasswordMut.mutate(invitePending.id)}
              disabled={inviteMut.isPending || setPasswordMut.isPending}
              title="Bypass the email — generate a temporary password the supervisor reads to the officer."
            >
              {setPasswordMut.isPending ? "Setting…" : "Set password manually"}
            </Button>
            <Button
              onClick={() => invitePending && inviteMut.mutate(invitePending.id)}
              disabled={inviteMut.isPending || setPasswordMut.isPending}
            >
              {inviteMut.isPending ? "Sending…" : "Send invite"}
            </Button>
          </>
        }
      >
        {invitePending && (
          <div className="text-sm text-slate-700 space-y-2">
            <p>
              Send an invite email to{" "}
              <strong>{invitePending.firstName} {invitePending.lastName}</strong> at{" "}
              <strong>{invitePending.emails?.[0]}</strong>?
            </p>
            <p className="text-slate-500 text-xs">
              They'll receive a link to set their password and sign in at the{" "}
              <strong>{LEVEL_LABEL[invitePending.level]}</strong> access level.
            </p>
            {invitePending.invitedAt && (
              <p className="text-amber-700 text-xs">
                Last invited {new Date(invitePending.invitedAt).toLocaleString()}.
              </p>
            )}
            <div className="mt-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <strong className="text-slate-800">Email scanner eating the link?</strong>{" "}
              Use <em>Set password manually</em> instead — we'll generate a
              one-time password to read to the officer; they sign in with their
              email + that password.
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={setPwResult !== null}
        onClose={() => setSetPwResult(null)}
        title="Temporary password ready"
        footer={
          <Button onClick={() => setSetPwResult(null)}>Done</Button>
        }
      >
        {setPwResult && (
          <div className="text-sm text-slate-700 space-y-3">
            <p>
              Read this password to the officer (Signal, phone, in person —
              not over the same email that's eating the invite links).
              They sign in with their email and this password.
            </p>
            <div className="rounded border border-slate-300 bg-slate-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Email
              </div>
              <div className="font-mono text-sm text-slate-900">{setPwResult.email}</div>
              <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Temporary password
              </div>
              <div className="font-mono text-base font-semibold text-slate-900 select-all">
                {setPwResult.password}
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Encourage them to change it from the profile menu after their first sign-in.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Compact chip on the roster row showing where this officer is in
// the invite lifecycle:
//   Accepted    — there's a profile linked to this employee with a
//                 confirmed email (trigger filled accepted_at).
//   Pending     — invitedAt is set but no confirmed profile yet,
//                 OR a profile exists without accepted_at.
//   Not invited — neither invitedAt nor a linked profile.
//   Disabled    — profile flag flipped; sign-in is blocked.
function InviteStatusChip({
  invitedAt, user,
}: {
  invitedAt: string | null;
  user: User | undefined;
}) {
  let label: string;
  let cls: string;
  let title: string;

  if (user?.disabledAt) {
    label = "Disabled";
    cls = "bg-slate-100 text-slate-600 border-slate-300";
    title = `Account disabled ${new Date(user.disabledAt).toLocaleDateString()}`;
  } else if (user?.acceptedAt) {
    label = "Accepted";
    cls = "bg-emerald-100 text-emerald-800 border-emerald-300";
    title = `Invite accepted ${new Date(user.acceptedAt).toLocaleDateString()}`;
  } else if (invitedAt || user) {
    label = "Pending";
    cls = "bg-amber-100 text-amber-800 border-amber-300";
    title = invitedAt
      ? `Invite sent ${new Date(invitedAt).toLocaleDateString()}, awaiting acceptance`
      : "Invite sent, awaiting acceptance";
  } else {
    label = "Not invited";
    cls = "bg-slate-50 text-slate-500 border-slate-200";
    title = "No invite sent yet";
  }

  return (
    <span
      className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded border ${cls}`}
      title={title}
    >
      {label}
    </span>
  );
}

// Level picker respecting the hierarchy rules. Disabled when the
// current acting user has no authority to change the target's level.
// Two-step remove: first click arms (turns red, shows "Confirm?"),
// second click deletes. Avoids relying on window.confirm() which some
// browsers / extensions silently suppress.
function ContactList({
  label, type, values, onChange, placeholder,
}: {
  label: string;
  type: "email" | "tel";
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const list = values.length === 0 ? [""] : values;
  function updateAt(i: number, v: string) {
    const next = [...list];
    next[i] = v;
    onChange(next.filter((_, idx) => idx !== next.length - 1 ? true : true));
  }
  function removeAt(i: number) {
    const next = list.filter((_, idx) => idx !== i);
    onChange(next);
  }
  function addAnother() { onChange([...list, ""]); }

  return (
    <div>
      <div className="block text-xs font-medium text-slate-600 mb-1">
        {label}{values.length > 1 ? ` (${values.length})` : ""}
      </div>
      <div className="space-y-1.5">
        {list.map((v, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input
              type={type}
              value={v}
              onChange={(e) => updateAt(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            {list.length > 1 && (
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="text-slate-400 hover:text-red-600 text-sm px-1"
                title="Remove"
              >×</button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addAnother}
          className="text-xs text-sky-700 hover:underline"
        >
          + Add another {label.toLowerCase()}
        </button>
      </div>
    </div>
  );
}

function LevelSelect({
  value, targetLevel, onChange,
}: {
  value: UserLevel;
  targetLevel: UserLevel;
  onChange: (lv: UserLevel) => void;
}) {
  const me = useEffectiveUser();
  const levels: UserLevel[] = ["l1", "l2", "l3", "l4"];
  const allowed = levels.filter(
    (lv) => can.setEmployeeLevel(me, targetLevel, lv) || lv === value,
  );
  const canEdit = allowed.length > 1
    && can.setEmployeeLevel(me, targetLevel, value === "l4" ? "l3" : "l4");
  return (
    <Select
      value={value}
      disabled={!canEdit}
      onChange={(e) => onChange(e.target.value as UserLevel)}
    >
      {value === "owner" && <option value="owner">{LEVEL_LABEL.owner}</option>}
      {allowed.map((lv) => (
        <option key={lv} value={lv}>{LEVEL_LABEL[lv]}</option>
      ))}
    </Select>
  );
}

function PhotoUpload({
  photo, firstName, lastName, onChange,
}: {
  photo: string | null;
  firstName: string;
  lastName: string;
  onChange: (dataUrl: string | null) => void;
}) {
  async function handleFile(f: File) {
    if (!f.type.startsWith("image/")) {
      alert("Please pick an image.");
      return;
    }
    // Downscale to at most 512px on the long edge so rows stay
    // manageable in localStorage caches and the DB row doesn't
    // balloon past a few hundred KB per employee.
    const dataUrl = await resizeToDataUrl(f, 512);
    onChange(dataUrl);
  }
  return (
    <div className="flex items-center gap-4 border border-slate-200 rounded p-3 bg-slate-50">
      <Avatar photo={photo} firstName={firstName} lastName={lastName} size={72} />
      <div className="flex-1 space-y-1">
        <div className="text-sm font-medium text-slate-900">Photo</div>
        <div className="text-xs text-slate-500">
          Optional headshot or badge photo. Shown as an avatar in the
          roster and on the employee's own profile.
        </div>
        <div className="flex gap-2 pt-1">
          <label className="inline-flex items-center justify-center font-medium rounded-md px-2.5 py-1 text-xs bg-white border border-slate-300 hover:bg-slate-50 cursor-pointer">
            {photo ? "Replace" : "Upload"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
                e.target.value = "";
              }}
            />
          </label>
          {photo && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="inline-flex items-center justify-center font-medium rounded-md px-2.5 py-1 text-xs bg-white border border-slate-300 text-slate-600 hover:bg-slate-50"
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

async function resizeToDataUrl(file: File, maxEdge: number): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.85);
}

// Days-off editor: 7 weekday checkboxes (Sun–Sat, matching
// JS Date.getDay() indices 0–6) and an effective start date.
// Supervisors set this per-officer; saving materializes XXX
// assignments on matching weekdays for ~a year, skipping dates
// already explicitly assigned so manual edits survive.
// Two-pattern, optional-two-week days-off editor.
//
// Each pattern (regular + tac shift) carries a mandatory Week 1
// and an optional Week 2. Toggling the "Include week 2" checkbox
// arms / disarms a 14-day rotation; with it off, Week 1 repeats
// every week. A mode toggle at the top picks which of the two
// patterns is the *active* one — i.e. the one applyDaysOffPattern
// writes onto the schedule. The inactive pattern is stored
// anyway so the supervisor can flip back without re-entering days.
function DaysOffEditor({
  regularW1, regularW2, tacW1, tacW2, mode, startDate, onChange,
}: {
  regularW1: number[];
  regularW2: number[] | null;
  tacW1: number[];
  tacW2: number[] | null;
  mode: "regular" | "tac";
  startDate: string;
  onChange: (patch: Partial<{
    daysOffWeekdays: number[];
    daysOffWeekdaysW2: number[] | null;
    tacDaysOffWeekdays: number[];
    tacDaysOffWeekdaysW2: number[] | null;
    daysOffMode: "regular" | "tac";
    daysOffStartDate: string | null;
  }>) => void;
}) {
  const activeW1 = mode === "tac" ? tacW1 : regularW1;
  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-medium text-slate-900">Days off</div>
        <div className="text-xs text-slate-500">
          Auto-fill XXX on these weekdays from the start date forward
          (up to a year). Manual edits like TR or V on an off-day are
          preserved when the pattern is re-applied. Two patterns are
          stored — Regular and 12-hour Shift — and the selector below
          picks which one materialises onto the schedule.
        </div>
      </div>

      {/* Active-pattern toggle. The radio-style segmented control
        * makes the two-state choice (regular vs tac) obvious; the
        * unselected one is the "stored but inactive" pattern. */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">Active pattern</span>
        <div className="inline-flex rounded border border-slate-300 overflow-hidden">
          <button
            type="button"
            onClick={() => onChange({ daysOffMode: "regular" })}
            className={
              "px-3 py-1 font-medium " +
              (mode === "regular"
                ? "bg-slate-800 text-white"
                : "bg-white text-slate-700 hover:bg-slate-50")
            }
          >
            Regular
          </button>
          <button
            type="button"
            onClick={() => onChange({ daysOffMode: "tac" })}
            className={
              "px-3 py-1 font-medium border-l border-slate-300 " +
              (mode === "tac"
                ? "bg-slate-800 text-white"
                : "bg-white text-slate-700 hover:bg-slate-50")
            }
          >
            12-hour Shift
          </button>
        </div>
        <span className="ml-auto flex items-center gap-2">
          <label className="text-slate-500">Start</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => onChange({ daysOffStartDate: e.target.value || null })}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </span>
      </div>

      <PatternBlock
        title="Regular days off"
        dim={mode !== "regular"}
        week1={regularW1}
        week2={regularW2}
        onChange={(w1, w2) => onChange({
          daysOffWeekdays: w1,
          daysOffWeekdaysW2: w2,
        })}
      />
      <PatternBlock
        title="12-hour Shift days off"
        dim={mode !== "tac"}
        week1={tacW1}
        week2={tacW2}
        onChange={(w1, w2) => onChange({
          tacDaysOffWeekdays: w1,
          tacDaysOffWeekdaysW2: w2,
        })}
      />

      {activeW1.length > 0 && !startDate && (
        <p className="text-xs text-amber-700">
          Pick a start date so the pattern can be applied.
        </p>
      )}
    </div>
  );
}

// One stored pattern (Regular or Tac). Week 1 is mandatory, Week 2
// is opt-in via the checkbox — turning it on swaps the rotation
// from weekly to 14-day, with Week 1 / Week 2 alternating from
// startDate forward. `dim` greys out the block when the parent's
// mode points at the *other* pattern, so the supervisor can see
// which one is live without losing the inactive settings.
function PatternBlock({
  title, dim, week1, week2, onChange,
}: {
  title: string;
  dim: boolean;
  week1: number[];
  week2: number[] | null;
  onChange: (week1: number[], week2: number[] | null) => void;
}) {
  const LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const w1Set = new Set(week1);
  const hasW2 = week2 !== null;
  const w2Set = new Set(week2 ?? []);
  function toggleW1(idx: number) {
    const next = new Set(w1Set);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    onChange(Array.from(next).sort((a, b) => a - b), week2);
  }
  function toggleW2(idx: number) {
    const next = new Set(w2Set);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    onChange(week1, Array.from(next).sort((a, b) => a - b));
  }
  function toggleEnableW2(on: boolean) {
    onChange(week1, on ? [] : null);
  }
  return (
    <div className={
      "rounded border border-slate-200 p-3 space-y-2 transition " +
      (dim ? "opacity-60 bg-slate-50" : "bg-white")
    }>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">
        {title}
      </div>
      <Row
        label="Week 1"
        weekdaysSet={w1Set}
        onToggle={toggleW1}
        labels={LABELS}
      />
      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
        <input
          type="checkbox"
          className="h-3.5 w-3.5"
          checked={hasW2}
          onChange={(e) => toggleEnableW2(e.target.checked)}
        />
        Include week 2 (14-day rotation)
      </label>
      {hasW2 && (
        <Row
          label="Week 2"
          weekdaysSet={w2Set}
          onToggle={toggleW2}
          labels={LABELS}
        />
      )}
    </div>
  );
}

function Row({
  label, weekdaysSet, onToggle, labels,
}: {
  label: string;
  weekdaysSet: Set<number>;
  onToggle: (idx: number) => void;
  labels: string[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] text-slate-500 w-12 shrink-0">{label}</span>
      {labels.map((dayLabel, i) => {
        const active = weekdaysSet.has(i);
        return (
          <button
            key={i}
            type="button"
            onClick={() => onToggle(i)}
            className={
              "px-2.5 py-1 rounded-md text-xs font-semibold border transition " +
              (active
                ? "bg-slate-800 text-white border-slate-800"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50")
            }
          >
            {dayLabel}
          </button>
        );
      })}
    </div>
  );
}

// Tri-state header checkbox. Renders unchecked when nothing is
// selected, indeterminate when only some visible rows are selected,
// checked when every visible row is selected. Clicking flips the
// whole visible set on or off — useful when a supervisor wants to
// bulk-update everyone showing on the current view.
function SelectAllCheckbox({
  visibleIds, selected, onToggleAll,
}: {
  visibleIds: string[];
  selected: Set<string>;
  onToggleAll: (ids: string[], on: boolean) => void;
}) {
  const selectedCount = visibleIds.filter((id) => selected.has(id)).length;
  const allOn = visibleIds.length > 0 && selectedCount === visibleIds.length;
  const someOn = selectedCount > 0 && !allOn;
  return (
    <input
      type="checkbox"
      checked={allOn}
      ref={(el) => { if (el) el.indeterminate = someOn; }}
      onChange={() => onToggleAll(visibleIds, !allOn)}
      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
      aria-label={allOn ? "Deselect all" : "Select all"}
    />
  );
}

// ----------------------------------------------------------------------
// Mass-edit modal.
//
// Every field is opt-in: the supervisor ticks "Apply" next to the
// fields they want to change, fills them in, and only the ticked
// fields get sent to updateEmployee for each selected officer.
// Untouched fields stay exactly as they were on each row.
//
// Identity fields (name, badge, photo, emails, phones) are
// intentionally absent — those are per-person and don't make
// sense to mass-set. Everything else from the single-edit form is
// here, plus the same days-off editor so a shift bid can be
// applied across a whole platoon in one click.
type MassField =
  | "rank" | "status" | "level" | "isOic" | "isFto"
  | "departmentId" | "homeShiftId"
  | "daysOff"     // bundles all six daysOff* keys; toggled together
  | "followsTacMode" | "tacHomeShiftId";

function MassEditModal({
  open, count, employeeIds, departments, shifts, pending, onClose, onApply,
}: {
  open: boolean;
  count: number;
  // Mirrors the count selection — passed through so the embedded
  // ClearProceduralShifts component knows which officers to wipe
  // when the supervisor uses the inline "clear procedural shifts"
  // button. Kept as an array so it stays stable across renders.
  employeeIds: string[];
  departments: { id: string; name: string }[];
  shifts: Shift[];
  pending: boolean;
  onClose: () => void;
  onApply: (patch: Partial<Employee>) => void;
}) {
  const [apply, setApply] = useState<Record<MassField, boolean>>({
    rank: false, status: false, level: false, isOic: false, isFto: false,
    departmentId: false, homeShiftId: false,
    daysOff: false,
    followsTacMode: false, tacHomeShiftId: false,
  });
  const [draft, setDraft] = useState<Partial<Employee>>({
    rank: "",
    status: "active",
    level: "l4",
    isOic: false,
    isFto: false,
    departmentId: "",
    homeShiftId: null,
    daysOffWeekdays: [],
    daysOffWeekdaysW2: null,
    tacDaysOffWeekdays: [],
    tacDaysOffWeekdaysW2: null,
    daysOffMode: "regular",
    daysOffStartDate: "",
    followsTacMode: false,
    tacHomeShiftId: null,
  });

  function flip(k: MassField) {
    setApply((prev) => ({ ...prev, [k]: !prev[k] }));
  }

  function submit() {
    const patch: Partial<Employee> = {};
    if (apply.rank) patch.rank = draft.rank?.trim() || null;
    if (apply.status) patch.status = draft.status;
    if (apply.level) patch.level = draft.level;
    if (apply.isOic) patch.isOic = draft.isOic;
    if (apply.isFto) patch.isFto = draft.isFto;
    if (apply.departmentId) patch.departmentId = draft.departmentId || undefined;
    if (apply.homeShiftId) patch.homeShiftId = draft.homeShiftId ?? null;
    if (apply.daysOff) {
      patch.daysOffWeekdays = draft.daysOffWeekdays ?? [];
      patch.daysOffWeekdaysW2 = draft.daysOffWeekdaysW2 ?? null;
      patch.tacDaysOffWeekdays = draft.tacDaysOffWeekdays ?? [];
      patch.tacDaysOffWeekdaysW2 = draft.tacDaysOffWeekdaysW2 ?? null;
      patch.daysOffMode = draft.daysOffMode ?? "regular";
      patch.daysOffStartDate = draft.daysOffStartDate || null;
    }
    if (apply.followsTacMode) patch.followsTacMode = draft.followsTacMode;
    if (apply.tacHomeShiftId) patch.tacHomeShiftId = draft.tacHomeShiftId ?? null;
    onApply(patch);
  }

  const anyApply = Object.values(apply).some(Boolean);
  const shiftsForDept = (draft.departmentId
    ? shifts.filter((s) => s.departmentId === draft.departmentId)
    : shifts);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${count} employee${count === 1 ? "" : "s"}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !anyApply}>
            {pending ? "Applying…" : `Apply to ${count}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          Tick "Apply" next to each field you want to change. Untouched
          fields stay as they are on every selected officer.
        </p>

        <MassRow label="Rank" applied={apply.rank} onToggle={() => flip("rank")}>
          <Input
            value={draft.rank ?? ""}
            disabled={!apply.rank}
            onChange={(e) => setDraft({ ...draft, rank: e.target.value })}
          />
        </MassRow>

        <MassRow label="Status" applied={apply.status} onToggle={() => flip("status")}>
          <Select
            value={draft.status ?? "active"}
            disabled={!apply.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value as Employee["status"] })}
          >
            <option value="active">Active</option>
            <option value="leave">On leave</option>
            <option value="inactive">Inactive</option>
          </Select>
        </MassRow>

        <MassRow label="Access level" applied={apply.level} onToggle={() => flip("level")}>
          <Select
            value={draft.level ?? "l4"}
            disabled={!apply.level}
            onChange={(e) => setDraft({ ...draft, level: e.target.value as UserLevel })}
          >
            <option value="l1">{LEVEL_LABEL.l1}</option>
            <option value="l2">{LEVEL_LABEL.l2}</option>
            <option value="l3">{LEVEL_LABEL.l3}</option>
            <option value="l4">{LEVEL_LABEL.l4}</option>
          </Select>
        </MassRow>

        <MassRow label="OIC" applied={apply.isOic} onToggle={() => flip("isOic")}>
          <Select
            value={draft.isOic ? "yes" : "no"}
            disabled={!apply.isOic}
            onChange={(e) => setDraft({ ...draft, isOic: e.target.value === "yes" })}
          >
            <option value="no">Not OIC</option>
            <option value="yes">Officer in Charge</option>
          </Select>
        </MassRow>

        <MassRow label="FTO" applied={apply.isFto} onToggle={() => flip("isFto")}>
          <Select
            value={draft.isFto ? "yes" : "no"}
            disabled={!apply.isFto}
            onChange={(e) => setDraft({ ...draft, isFto: e.target.value === "yes" })}
          >
            <option value="no">Not in training</option>
            <option value="yes">In training (not counted toward coverage)</option>
          </Select>
        </MassRow>

        <MassRow label="Department" applied={apply.departmentId} onToggle={() => flip("departmentId")}>
          <Select
            value={draft.departmentId ?? ""}
            disabled={!apply.departmentId}
            onChange={(e) => setDraft({ ...draft, departmentId: e.target.value, homeShiftId: null })}
          >
            <option value="" disabled>— Pick —</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </Select>
          {apply.departmentId && (
            <p className="mt-1 text-xs text-amber-700">
              Changing department clears each officer's home shift unless
              you also tick "Home shift" below — shifts are dept-scoped.
            </p>
          )}
        </MassRow>

        <MassRow label="Home shift" applied={apply.homeShiftId} onToggle={() => flip("homeShiftId")}>
          <Select
            value={draft.homeShiftId ?? ""}
            disabled={!apply.homeShiftId}
            onChange={(e) => setDraft({ ...draft, homeShiftId: e.target.value || null })}
          >
            <option value="">— None —</option>
            {shiftsForDept.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </MassRow>

        <MassRow label="Uses 12-hour Shifts" applied={apply.followsTacMode} onToggle={() => flip("followsTacMode")}>
          <Select
            value={draft.followsTacMode ? "yes" : "no"}
            disabled={!apply.followsTacMode}
            onChange={(e) => setDraft({ ...draft, followsTacMode: e.target.value === "yes" })}
          >
            <option value="no">No</option>
            <option value="yes">Yes — swap to 12-hour shift when 12-hour Shifts are active</option>
          </Select>
        </MassRow>

        <MassRow label="12-hour home shift" applied={apply.tacHomeShiftId} onToggle={() => flip("tacHomeShiftId")}>
          <Select
            value={draft.tacHomeShiftId ?? ""}
            disabled={!apply.tacHomeShiftId}
            onChange={(e) => setDraft({ ...draft, tacHomeShiftId: e.target.value || null })}
          >
            <option value="">— None —</option>
            {shifts.filter((s) => s.tacRole === "tac").map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </MassRow>

        <div className="border-t border-slate-200 pt-3">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <input
              type="checkbox"
              checked={apply.daysOff}
              onChange={() => flip("daysOff")}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Apply days-off pattern
          </label>
          {apply.daysOff && (
            <div className="mt-3 pl-6">
              <DaysOffEditor
                regularW1={draft.daysOffWeekdays ?? []}
                regularW2={draft.daysOffWeekdaysW2 ?? null}
                tacW1={draft.tacDaysOffWeekdays ?? []}
                tacW2={draft.tacDaysOffWeekdaysW2 ?? null}
                mode={draft.daysOffMode ?? "regular"}
                startDate={draft.daysOffStartDate ?? ""}
                onChange={(patch) => setDraft({ ...draft, ...patch })}
              />
            </div>
          )}
        </div>

        {employeeIds.length > 0 && (
          <div className="border-t border-slate-200 pt-3">
            <ClearProceduralShifts
              employeeIds={employeeIds}
              label={`Clear procedural shifts for ${employeeIds.length} officer${employeeIds.length === 1 ? "" : "s"}`}
              onCleared={onClose}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

function MassRow({
  label, applied, onToggle, children,
}: {
  label: string;
  applied: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <label className="flex items-center gap-2 pt-1.5 w-44 shrink-0 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={applied}
          onChange={onToggle}
          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        <span className="font-medium">{label}</span>
      </label>
      <div className={`flex-1 ${applied ? "" : "opacity-60"}`}>
        {children}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Clear procedural shifts — wipes each officer's homeShiftId and
// days-off pattern, then deletes every from_pattern=true assignment
// row dated on/after the chosen start date. Past rows stay intact
// for history. Used when an officer is moving units, leaving the
// agency, retiring, or whenever the bid is being torn down ahead of
// a new one being drafted.
//
// Shared by both the single-employee edit modal (one id) and the
// MassEditModal (N ids). The component owns the date picker and the
// confirmation step; the parent only needs to hand in the ids.
function ClearProceduralShifts({
  employeeIds, label, onCleared,
}: {
  employeeIds: string[];
  label: string;
  // Fired after the operation completes successfully. The parent
  // typically uses this to dismiss the surrounding modal.
  onCleared?: () => void;
}) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{ count: number; deleted: number } | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const clearMut = useMutation({
    mutationFn: async () => {
      let count = 0;
      let deleted = 0;
      for (const id of employeeIds) {
        const r = await api.clearProceduralShifts({
          employeeId: id, fromDate,
        });
        count += 1;
        deleted += r.deletedRows;
      }
      return { count, deleted };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      setResult(r);
      setLocalError(null);
      setConfirming(false);
      onCleared?.();
    },
    onError: (e: { message?: string }) => {
      setLocalError(e?.message ?? "Could not clear");
      setConfirming(false);
    },
  });

  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">
          Removes each officer's home shift + days-off pattern, then
          deletes their scheduled XXX rows on and after the start
          date. Past days and any manually-typed cells (OT, TR, S,
          V, …) are left alone.
        </div>
      </div>
      <div className="flex items-end gap-2">
        <Field label="Start date">
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </Field>
        <Button
          variant="danger"
          disabled={
            clearMut.isPending || !fromDate || employeeIds.length === 0
          }
          onClick={() => setConfirming(true)}
        >
          {clearMut.isPending ? "Clearing…" : "Clear forward"}
        </Button>
      </div>
      {localError && (
        <div className="text-sm text-red-600">{localError}</div>
      )}
      {result && (
        <div className="text-sm text-emerald-700">
          Cleared {result.count} officer{result.count === 1 ? "" : "s"}{" "}
          and removed {result.deleted} scheduled day{result.deleted === 1 ? "" : "s"}.
        </div>
      )}

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Clear procedural shifts?"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirming(false)}
              disabled={clearMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => clearMut.mutate()}
              disabled={clearMut.isPending}
            >
              {clearMut.isPending ? "Clearing…" : "Confirm clear"}
            </Button>
          </>
        }
      >
        <div className="text-sm text-slate-700 space-y-2">
          <p>
            This will clear the home shift and days-off pattern for{" "}
            <span className="font-semibold">{employeeIds.length}</span>{" "}
            officer{employeeIds.length === 1 ? "" : "s"} and remove
            every scheduled day-off row on or after{" "}
            <span className="font-semibold">{fromDate}</span>.
          </p>
          <p className="text-xs text-slate-500">
            History before that date stays intact. Manually-entered
            codes (OT, S, V, custom notes) are not deleted.
          </p>
        </div>
      </Modal>
    </div>
  );
}

// ----------------------------------------------------------------------
// Temporary shift assignments — date-range placements that override
// homeShiftId for a window. List existing rows for this officer
// (any that haven't ended yet) and a + add form for a new one.

function ShiftAssignmentsEditor({
  employeeId, shifts,
}: {
  employeeId: string;
  shifts: Shift[];
}) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const items = useQuery({
    queryKey: ["shiftAssignments", "employee", employeeId],
    queryFn: () => api.listShiftAssignments(today),
  });
  const mine = (items.data ?? []).filter((sa) => sa.employeeId === employeeId);

  const [draft, setDraft] = useState<{
    shiftId: string; startDate: string; endDate: string; note: string;
  }>({ shiftId: "", startDate: today, endDate: today, note: "" });
  const [localError, setLocalError] = useState<string | null>(null);

  function invalidateAfter() {
    // Refresh the per-officer list above plus any open schedule
    // grid (its `["shiftAssignments", startStr, endStr]` query keys
    // don't share a prefix with ours, so invalidate broadly).
    qc.invalidateQueries({ queryKey: ["shiftAssignments"] });
    qc.invalidateQueries({ queryKey: ["assignments"] });
    qc.invalidateQueries({ queryKey: ["coverage"] });
  }
  const createMut = useMutation({
    mutationFn: () => api.createShiftAssignment({
      employeeId,
      shiftId: draft.shiftId,
      startDate: draft.startDate,
      endDate: draft.endDate,
      note: draft.note.trim() || null,
    }),
    onSuccess: () => {
      invalidateAfter();
      setDraft({ shiftId: "", startDate: today, endDate: today, note: "" });
      setLocalError(null);
    },
    onError: (e: { message?: string }) =>
      setLocalError(e?.message ?? "Could not save"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteShiftAssignment(id),
    onSuccess: () => invalidateAfter(),
  });

  const shiftById = useMemo(
    () => new Map(shifts.map((s) => [s.id, s])),
    [shifts],
  );

  function submit() {
    if (!draft.shiftId) { setLocalError("Pick a shift."); return; }
    if (!draft.startDate || !draft.endDate) {
      setLocalError("Pick a start and end date.");
      return;
    }
    if (draft.endDate < draft.startDate) {
      setLocalError("End date must be on or after start date.");
      return;
    }
    createMut.mutate();
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-medium text-slate-900">
          Temporary shift assignments
        </div>
        <div className="text-xs text-slate-500">
          Override this officer's home shift for a date range — e.g.
          working Nights for a week while normally on Days. The
          schedule grid surfaces them under both shifts and greys out
          the off-week cells. Past assignments (end date before today)
          are hidden.
        </div>
      </div>

      {mine.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded border border-slate-200 bg-white">
          {mine.map((sa) => {
            const s = shiftById.get(sa.shiftId);
            return (
              <li key={sa.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="font-medium" style={{ color: s?.color ?? undefined }}>
                  ● {s?.name ?? sa.shiftId.slice(0, 8)}
                </span>
                <span className="text-slate-500">
                  {sa.startDate} → {sa.endDate}
                </span>
                {sa.note && (
                  <span className="text-xs text-slate-500 italic truncate">· {sa.note}</span>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  onClick={() => deleteMut.mutate(sa.id)}
                  disabled={deleteMut.isPending}
                >
                  Remove
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="rounded border border-dashed border-slate-300 p-3 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Field label="Shift">
            <Select
              value={draft.shiftId}
              onChange={(e) => setDraft({ ...draft, shiftId: e.target.value })}
            >
              <option value="">— Pick —</option>
              {shifts.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Start">
            <Input
              type="date"
              value={draft.startDate}
              onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
            />
          </Field>
          <Field label="End">
            <Input
              type="date"
              value={draft.endDate}
              onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Note (optional)">
          <Input
            value={draft.note}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            placeholder="Filling in for Smith on vacation"
          />
        </Field>
        {localError && <div className="text-sm text-red-600">{localError}</div>}
        <div className="flex justify-end">
          <Button onClick={submit} disabled={createMut.isPending}>
            {createMut.isPending ? "Adding…" : "Add assignment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// IOD periods — per-officer Injury-On-Duty ranges. Start date is
// required, end date is optional (null = open, fills forward
// through the year horizon). Cells render IOD on every day in the
// range, replacing pattern XXX where they overlap. Manual edits
// survive intact via the existing from_pattern dedupe.
function IodPeriodsEditor({ employee }: { employee: Employee }) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const items = useQuery({
    queryKey: ["iodPeriods", "employee", employee.id],
    queryFn: () => api.listIodPeriods(),
  });
  const mine = (items.data ?? []).filter((p) => p.employeeId === employee.id);

  const [draft, setDraft] = useState<{
    startDate: string; endDate: string; note: string;
  }>({ startDate: today, endDate: "", note: "" });
  const [localError, setLocalError] = useState<string | null>(null);

  function invalidateAfter() {
    qc.invalidateQueries({ queryKey: ["iodPeriods"] });
    qc.invalidateQueries({ queryKey: ["assignments"] });
    qc.invalidateQueries({ queryKey: ["coverage"] });
  }

  // Re-run the days-off materialiser so IOD rows land on (or come
  // off) the schedule immediately. Falls back to today as the
  // start when the officer has no daysOffStartDate on file.
  async function rematerialise() {
    const isTac = employee.daysOffMode === "tac";
    const target = isTac
      ? (employee.tacHomeShiftId ?? employee.homeShiftId)
      : (employee.homeShiftId ?? employee.tacHomeShiftId);
    const w1 = (isTac ? employee.tacDaysOffWeekdays : employee.daysOffWeekdays) ?? [];
    const w2 = isTac ? employee.tacDaysOffWeekdaysW2 : employee.daysOffWeekdaysW2;
    const sd = employee.daysOffStartDate ?? today;
    if (sd) {
      await api.applyDaysOffPattern({
        employeeId: employee.id, week1: w1, week2: w2, startDate: sd,
        homeShiftId: target, silent: true,
      });
    }
  }

  const createMut = useMutation({
    mutationFn: async () => {
      const created = await api.createIodPeriod({
        employeeId: employee.id,
        startDate: draft.startDate,
        endDate: draft.endDate || null,
        note: draft.note.trim() || null,
      });
      await rematerialise();
      return created;
    },
    onSuccess: () => {
      invalidateAfter();
      setDraft({ startDate: today, endDate: "", note: "" });
      setLocalError(null);
    },
    onError: (e: { message?: string }) =>
      setLocalError(e?.message ?? "Could not save"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await api.deleteIodPeriod(id);
      await rematerialise();
    },
    onSuccess: () => invalidateAfter(),
  });

  function submit() {
    if (!draft.startDate) { setLocalError("Pick a start date."); return; }
    if (draft.endDate && draft.endDate < draft.startDate) {
      setLocalError("End date must be on or after start date.");
      return;
    }
    createMut.mutate();
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-medium text-slate-900">
          Injury on Duty (IOD)
        </div>
        <div className="text-xs text-slate-500">
          Fill every cell with IOD between the start and end dates.
          Leave end blank for an open range — it stays in effect
          until you set an end date or remove the row. Works for
          unassigned officers too: their row in the UNASSIGNED
          section will show IOD across the range even without a
          home shift.
        </div>
      </div>

      {mine.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded border border-slate-200 bg-white">
          {mine.map((p) => (
            <li key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm">
              <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-800 border border-amber-200">
                IOD
              </span>
              <span className="text-slate-700">
                {p.startDate} → {p.endDate ?? "ongoing"}
              </span>
              {p.note && (
                <span className="text-xs text-slate-500 italic truncate">· {p.note}</span>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                onClick={() => deleteMut.mutate(p.id)}
                disabled={deleteMut.isPending}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded border border-dashed border-slate-300 p-3 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Field label="Start">
            <Input
              type="date"
              value={draft.startDate}
              onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
            />
          </Field>
          <Field label="End (optional)">
            <Input
              type="date"
              value={draft.endDate}
              onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
              placeholder="Leave blank for open"
            />
          </Field>
          <Field label="Note (optional)">
            <Input
              value={draft.note}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              placeholder="Case # or doctor note"
            />
          </Field>
        </div>
        {localError && <div className="text-sm text-red-600">{localError}</div>}
        <div className="flex justify-end">
          <Button onClick={submit} disabled={createMut.isPending}>
            {createMut.isPending ? "Adding…" : "Add IOD period"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Sortable-column plumbing for the employees table.

type SortKey =
  | "name" | "badge" | "radio" | "rank" | "status"
  | "oic"  | "level" | "shift" | "tacShift";
type SortDir = "asc" | "desc";

// Sort the roster in-memory. Empty cells sink to the bottom in both
// directions so a page of "—" cells doesn't push the officers you
// care about below the fold when you reverse-sort. Badge / Radio
// prefer numeric compare when both cells parse as numbers; falls
// back to a locale-aware natural sort otherwise.
function sortEmployees(
  employees: Employee[],
  shifts: Shift[],
  sort: { key: SortKey; dir: SortDir },
): Employee[] {
  const shiftName = new Map(shifts.map((s) => [s.id, s.name]));
  const flip = sort.dir === "asc" ? 1 : -1;
  const list = [...employees];
  list.sort((a, b) => {
    const { r, aEmpty, bEmpty } = compareFor(sort.key, a, b, shiftName);
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;   // empties always last
    if (bEmpty) return -1;
    return r * flip;
  });
  return list;
}

function compareFor(
  key: SortKey,
  a: Employee,
  b: Employee,
  shiftName: Map<string, string>,
): { r: number; aEmpty: boolean; bEmpty: boolean } {
  switch (key) {
    case "name": {
      const av = ((a.lastName ?? "") + " " + (a.firstName ?? "")).trim();
      const bv = ((b.lastName ?? "") + " " + (b.firstName ?? "")).trim();
      return {
        r: av.localeCompare(bv, undefined, { sensitivity: "base" }),
        aEmpty: !av,
        bEmpty: !bv,
      };
    }
    case "badge":
      return numericCompare(a.badgeNumber, b.badgeNumber);
    case "radio":
      return numericCompare(a.radioNumber, b.radioNumber);
    case "rank": {
      const av = (a.rank ?? "").trim();
      const bv = (b.rank ?? "").trim();
      return {
        r: av.localeCompare(bv, undefined, { sensitivity: "base" }),
        aEmpty: !av,
        bEmpty: !bv,
      };
    }
    case "status": {
      const av = (a.status ?? "").trim();
      const bv = (b.status ?? "").trim();
      return { r: av.localeCompare(bv), aEmpty: !av, bEmpty: !bv };
    }
    case "oic":
      // True first in ascending (OIC officers grouped up top).
      return { r: Number(!!b.isOic) - Number(!!a.isOic), aEmpty: false, bEmpty: false };
    case "level":
      // Rank order: owner (0) < l1 < l2 < l3 < l4 — ascending puts
      // owner at the top, matching the "seniority first" convention
      // everywhere else in the app.
      return { r: levelRank(a.level) - levelRank(b.level), aEmpty: false, bEmpty: false };
    case "shift": {
      const av = a.homeShiftId ? (shiftName.get(a.homeShiftId) ?? "") : "";
      const bv = b.homeShiftId ? (shiftName.get(b.homeShiftId) ?? "") : "";
      return { r: av.localeCompare(bv), aEmpty: !av, bEmpty: !bv };
    }
    case "tacShift": {
      // Officers who don't follow 12-hour mode aren't just sorted by
      // an absent tac shift — they're empty regardless of the id, so
      // they sink to the bottom same as officers missing a home
      // shift do above.
      const av = a.followsTacMode && a.tacHomeShiftId
        ? (shiftName.get(a.tacHomeShiftId) ?? "")
        : "";
      const bv = b.followsTacMode && b.tacHomeShiftId
        ? (shiftName.get(b.tacHomeShiftId) ?? "")
        : "";
      return { r: av.localeCompare(bv), aEmpty: !av, bEmpty: !bv };
    }
  }
}

function numericCompare(
  a: string | null | undefined,
  b: string | null | undefined,
): { r: number; aEmpty: boolean; bEmpty: boolean } {
  const av = (a ?? "").trim();
  const bv = (b ?? "").trim();
  if (!av || !bv) return { r: 0, aEmpty: !av, bEmpty: !bv };
  const an = Number(av);
  const bn = Number(bv);
  if (!Number.isNaN(an) && !Number.isNaN(bn)) {
    return { r: an - bn, aEmpty: false, bEmpty: false };
  }
  // At least one isn't numeric — fall back to natural (numeric-
  // aware) compare so "OIC10" > "OIC2" as expected.
  return {
    r: av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" }),
    aEmpty: false,
    bEmpty: false,
  };
}

// Clickable header cell. Click same key → flip direction; click a
// different key → reset to ascending. Renders an inactive ↕ hint on
// unsorted columns and the current ▲ / ▼ on the active one, so it's
// always visible which column is driving the order.
function SortHeader({
  label, sortKey, sort, onToggle,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onToggle: (k: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  const glyph = !active ? "↕" : sort.dir === "asc" ? "▲" : "▼";
  return (
    <th className="px-3 py-2 font-medium">
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className="inline-flex items-center gap-1 font-medium text-slate-600 hover:text-slate-900"
        aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      >
        <span>{label}</span>
        <span className={active ? "text-slate-500 text-[10px]" : "text-slate-300 text-[10px]"}>
          {glyph}
        </span>
      </button>
    </th>
  );
}
